import React from 'react';

/**
 * ErrorBoundary
 * Wraps the app so unexpected React exceptions don't produce a fully
 * blank screen. Shows a graceful fallback with a Reload button and — in
 * development — the actual error/stack for debugging.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Best-effort console log so it shows up in the browser console.
    // Avoid throwing here in case console is unavailable.
    try {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    } catch (e) { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (e) { /* ignore */ }
  };

  handleHome = () => {
    try { window.location.href = '/'; } catch (e) { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
    return (
      <div
        role="alert"
        className="min-h-screen bg-gray-50 flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-600 mb-6">
            An unexpected error interrupted this page. Your data is safe — auto-saved drafts
            (if any) are preserved. Please reload to continue.
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium hover:bg-gray-50"
              data-testid="error-boundary-home"
            >
              Go to home
            </button>
          </div>
          {isDev && this.state.error && (
            <pre className="mt-6 text-left text-xs bg-red-50 border border-red-200 rounded p-3 max-h-64 overflow-auto text-red-900">
              {String(this.state.error.stack || this.state.error.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
